import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Header, Footer, ScrollToTop } from './components/Header';
import heroImage from "../../assets/new_ui_img/hero_image.png";
import howitworksImage from "../../assets/new_ui_img/how_it_works_illustration.svg";
import entryImage from "../../assets/new_ui_img/entry_image.jpg";
import sysreqImage from "../../assets/new_ui_img/system_requirement_img.png";

// --- Scroll reveal hook ------------------------------------------------------
function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    document.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// --- Parallax hook ----------------------------------------------------------
function useParallax(ref, factor = 0.35) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handle = () => {
      const rect = el.getBoundingClientRect();
      const offset = (window.innerHeight / 2 - rect.top - rect.height / 2) * factor;
      const bg = el.querySelector(".parallax-bg");
      if (bg) bg.style.transform = `translateY(${offset}px)`;
    };
    window.addEventListener("scroll", handle, { passive: true });
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, [ref, factor]);
}

// --- Hero Section -------------------------------------------------------------
function HeroSection() {
  const sectionRef = useRef(null);
  useParallax(sectionRef, 0.3);

  return (
    <section ref={sectionRef} className="relative w-full h-screen min-h-[600px] max-h-[940px] overflow-hidden">
      <div className="parallax-bg absolute inset-0 will-change-transform hero-bg-animate"
        style={{ top: "-10%", height: "120%", transformOrigin: "center top" }}
      >
        <img src={heroImage} alt="Aerial view of parking lot" className="w-full h-full object-cover" />
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-[rgba(0,0,0,0.65)] via-[30%] to-transparent" />

      <div className="relative z-10 flex flex-col items-center h-full pt-36 px-1">
        <div className="text-center max-w-6xl mx-auto hero-title-animate">
          <h1 className="font-montserrat font-bold text-white leading-[1.3] text-[clamp(2.2rem,6vw,4.5rem)] whitespace-pre-wrap">
            Maximize your
            <br />
            Underutilized Parking Spaces
          </h1>
          <p className="mt-4 font-lato text-[#BBE8B1] text-[clamp(1.1rem,2.5vw,2.25rem)] leading-[1.4]">
            Turn every parking lot into a revenue-generating mobility hub
          </p>
        </div>

        <div className="mt-auto mb-8 lg:mb-12 bg-[rgba(0,0,0,0.8)] rounded-[10px] border border-[rgba(255,255,255,0.4)] px-6 py-6 lg:px-[50px] lg:py-[28px] w-full max-w-[1400px] flex flex-col lg:flex-row gap-8 lg:gap-12 items-start lg:items-center reveal delay-200">
          <p className="font-lato text-white text-[16px] lg:text-[18px] leading-[1.5] flex-1">
            An intelligent parking platform connecting parking owners, operators, and autonomous fleets through real-time availability and optimized utilization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
            <button className="bg-transparent border border-white text-white font-lato text-[1.1rem] px-6 py-3 lg:px-[30px] lg:py-[14px] rounded-[5px] hover:bg-white/10 transition-colors whitespace-nowrap">
              View Features
            </button>
            <NavLink to="/signup" className="bg-white text-[#0a66c2] font-lato text-[1.1rem] px-6 py-3 lg:px-[30px] lg:py-[14px] rounded-[5px] hover:bg-gray-100 transition-colors whitespace-nowrap flex items-center gap-3">
              Get Started Here
              <svg className="w-5 shrink-0" viewBox="0 0 20 14.73" fill="none">
                <path d="M1 6.36396C0.447715 6.36396 0 6.81168 0 7.36396C0 7.91625 0.447715 8.36396 1 8.36396V7.36396V6.36396ZM19.7071 8.07107C20.0976 7.68054 20.0976 7.04738 19.7071 6.65685L13.3431 0.292893C12.9526 -0.097631 12.3195 -0.097631 11.9289 0.292893C11.5384 0.683418 11.5384 1.31658 11.9289 1.70711L17.5858 7.36396L11.9289 13.0208C11.5384 13.4113 11.5384 14.0445 11.9289 14.435C12.3195 14.8256 12.9526 14.8256 13.3431 14.435L19.7071 8.07107ZM1 7.36396V8.36396H19V7.36396V6.36396H1V7.36396Z" fill="#0A66C2" />
              </svg>
            </NavLink>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- How It Works Section -----------------------------------------------------
function HowItWorksSection() {
  useReveal();

  return (
    <section className="bg-[#f5f5f5] py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="reveal mb-12 lg:mb-16">
          <h2 className="font-google-sans font-medium text-black text-[clamp(2rem,4vw,3.25rem)] leading-[1.3]"
            style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
            How it Works
          </h2>
          <p className="mt-3 font-lato text-[#6b6b6b] text-[clamp(1rem,1.6vw,1.25rem)] leading-[1.3]">
            In just a few steps, your parking lot becomes part of the autonomous mobility ecosystem.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          <div className="reveal-left bg-[#ebebeb] rounded-[10px] overflow-hidden flex items-center justify-center min-h-[320px] lg:min-h-[480px]">
            {/*<CarIllustration />*/}
            <img src={howitworksImage} alt="Car model" />
          </div>

          <div className="reveal-right flex flex-col gap-10 justify-center py-4 lg:py-8 lg:pl-6">
            <div className="delay-100">
              <h3 className="font-google-sans font-semibold text-black text-[clamp(1.1rem,1.8vw,1.5rem)] leading-[1.3] mb-3"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                Step 1 : Sign Up &amp; Configure
              </h3>
              <ul className="list-disc pl-6 font-lato text-[#616161] text-[clamp(0.9rem,1.2vw,1rem)] space-y-2 leading-[1.3]">
                <li>Create your free account and set designated spaces.</li>
                <li>Choose when and where Robotaxis can park � on your terms.</li>
              </ul>
            </div>

            <div className="delay-200">
              <h3 className="font-google-sans font-semibold text-black text-[clamp(1.1rem,1.8vw,1.5rem)] leading-[1.3] mb-3"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                Step 2 : We Connect Your Property
              </h3>
              <ul className="list-disc pl-6 font-lato text-[#616161] text-[clamp(0.9rem,1.2vw,1rem)] space-y-2 leading-[1.3]">
                <li>GeoParking verifies and publishes your parking inventory.</li>
                <li>Your property becomes available to participating autonomous fleet operators through our Fleet API.</li>
              </ul>
            </div>

            <div className="delay-300">
              <h3 className="font-google-sans font-semibold text-black text-[clamp(1.1rem,1.8vw,1.5rem)] leading-[1.3] mb-3"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                Step 3 : Earn &amp; Drive Sustainability
              </h3>
              <ul className="list-disc pl-6 font-lato text-[#616161] text-[clamp(0.9rem,1.2vw,1rem)] leading-[1.3]">
                <li>Your property becomes a zero-carbon transportation mode � benefiting both the environment and your community.</li>
                <li>Earning on unused parking.</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 delay-400">
              <NavLink to="/signup" className="bg-[#0a66c2] text-white font-lato text-[1.1rem] h-[50px] px-6 rounded-[5px] flex items-center gap-3 hover:bg-[#0856a8] transition-colors">
                Sign Up Today
                <svg className="w-5 shrink-0" viewBox="0 0 20 14.73" fill="none">
                  <path d="M1 6.36396C0.447715 6.36396 0 6.81168 0 7.36396C0 7.91625 0.447715 8.36396 1 8.36396V7.36396V6.36396ZM19.7071 8.07107C20.0976 7.68054 20.0976 7.04738 19.7071 6.65685L13.3431 0.292893C12.9526 -0.097631 12.3195 -0.097631 11.9289 0.292893C11.5384 0.683418 11.5384 1.31658 11.9289 1.70711L17.5858 7.36396L11.9289 13.0208C11.5384 13.4113 11.5384 14.0445 11.9289 14.435C12.3195 14.8256 12.9526 14.8256 13.3431 14.435L19.7071 8.07107ZM1 7.36396V8.36396H19V7.36396V6.36396H1V7.36396Z" fill="white" />
                </svg>
              </NavLink>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#707070" strokeWidth="1.5" />
                  <path d="M12 8V12L14 14" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                </svg>
                <span className="font-lato text-[#616161] text-[1rem]">8-minute Onboarding to complete steps</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Image Showcase Section ---------------------------------------------------
function ImageShowcaseSection() {
  const sectionRef = useRef(null);
  useParallax(sectionRef, 0.25);

  const images = [
    /*"https://images.unsplash.com/photo-1543465077-db45d34b88a5?w=1920&h=800&fit=crop&auto=format",
    "https://images.unsplash.com/photo-1697761221129-fdf528507890?w=1920&h=800&fit=crop&auto=format",
    "https://images.unsplash.com/photo-1536585422010-b58dd25bb946?w=1920&h=800&fit=crop&auto=format",
    "https://images.unsplash.com/photo-1616363088386-31c4a8414858?w=1920&h=800&fit=crop&auto=format",*/    
  ];

  <img src={entryImage} alt="Aerial view of parking lot" className="w-full h-full object-cover" />

  return (
    <section ref={sectionRef} className="relative w-full h-[400px] md:h-[560px] lg:h-[700px] overflow-hidden">
      <div className="parallax-bg absolute inset-0 will-change-transform" style={{ top: "-8%", height: "116%" }}>
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: i === images.length - 1 ? 1 : 0, zIndex: i }}
          />
        ))}
        <img
          src={entryImage}
          alt="Parking lot aerial view"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 5 }}
        />
      </div>
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/20 via-transparent to-black/30 pointer-events-none" />
    </section>
  );
}

// --- System Requirements Section ---------------------------------------------
function SystemRequirementsSection() {
  const sectionRef = useRef(null);
  useParallax(sectionRef, 0.2);

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden ">
      <div className="absolute left-0 top-0 bottom-0 w-full lg:w-[55%] overflow-hidden">
        <div className="parallax-bg absolute inset-0 will-change-transform" style={{ top: "-17%", height: "100%" }}>
          <img src={sysreqImage} alt="Parking management system" className="w-full h-full"/>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(245,245,245,0.1)] via-[rgba(245,245,245,0.5)] to-[#f5f5f5]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-16 lg:py-24 flex justify-end">
        <div className="w-full lg:w-[60%]">
          <div className="reveal">
            <h2 className="font-google-sans font-medium text-black text-[clamp(1.8rem,3.5vw,3.25rem)] leading-[1.3] text-right"
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              System Requirements
            </h2>
            <p className="mt-3 font-lato text-[#333] text-[clamp(0.95rem,1.5vw,1.25rem)] leading-[1.3] text-right">
              GeoParking is optimized for modern desktop browsers to deliver the best performance and reliability. Please review the recommended system requirements below before accessing the platform.
            </p>
          </div>

          <div className="reveal delay-100 mt-5 bg-[#e2f0fd] px-6 py-3 lg:px-[30px] lg:py-[14px] rounded-[5px] text-right">
            <p className="font-lato text-[#0a66c2] text-[clamp(0.85rem,1.2vw,1.1rem)] leading-[1.5]">
              Platform compatibility will continue to expand through future GeoParking software releases.
            </p>
          </div>

          <div className="reveal delay-200 mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-10 h-10 shrink-0" viewBox="0 0 40 40" fill="none">
                  <path d="M36.6667 21.6667C36.5368 24.7872 36.1183 26.7005 34.769 28.0473C32.813 30 29.6648 30 23.3683 30H16.69C10.3936 30 7.24542 30 5.28937 28.0473C3.33333 26.0948 3.33333 22.952 3.33333 16.6667C3.33333 10.3813 3.33333 7.23858 5.28937 5.28595C7.24542 3.33333 10.3936 3.33333 16.69 3.33333H18.3333" stroke="#388E3C" strokeLinecap="round" strokeWidth="2.5" />
                  <path d="M20 30V36.6667" stroke="#388E3C" strokeWidth="2.5" />
                  <path d="M13.3333 36.6667H26.6667" stroke="#388E3C" strokeLinecap="round" strokeWidth="2.5" />
                  <path d="M18.3333 25H21.6667" stroke="#388E3C" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d="M36.6667 9.16667V5C33.3333 5 30.8333 3.33333 30.8333 3.33333C30.8333 3.33333 28.3333 5 25 5V9.16667C25 15 30.8333 16.6667 30.8333 16.6667C30.8333 16.6667 36.6667 15 36.6667 9.16667Z" stroke="#388E3C" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                </svg>
                <h3 className="font-lato font-bold text-[#121212] text-[1.25rem] leading-[1.3]">Supported Platforms</h3>
              </div>
              <ul className="list-disc pl-5 font-google-sans text-[#686868] text-[clamp(0.9rem,1.1vw,1.1rem)] space-y-2 leading-[1.3]"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                <li>
                  <span className="font-lato text-[#121212]">Operating Systems:</span>
                  <span className="font-lato"> Windows 11, macOS Sequoia</span>
                </li>
                <li>
                  <span className="font-lato text-[#121212]">Browser:</span>
                  <span className="font-lato"> Chrome, Edge, Safari</span>
                </li>
                <li>
                  <span className="font-lato text-[#121212]">Hardware:</span>
                  <span className="font-lato"> 8 GB RAM minimum, Modern dual-core processor, Broadband internet (10 Mbps+)</span>
                </li>
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-10 h-10 shrink-0" viewBox="0 0 40 40" fill="none">
                  <path d="M21.6667 5.83333H18.3333C12.8336 5.83333 10.0837 5.83333 8.37522 7.54188C6.66667 9.25042 6.66667 12.0003 6.66667 17.5V25C6.66667 30.4997 6.66667 33.2495 8.37522 34.9582C10.0837 36.6667 12.8336 36.6667 18.3333 36.6667H23.3333L33.3333 26.6667V17.5C33.3333 12.0003 33.3333 9.25042 31.6248 7.54188C29.9162 5.83333 27.1663 5.83333 21.6667 5.83333Z" stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d="M13.3333 23.3333H19.1667M13.3333 16.6667H26.6667" stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d="M33.3333 26.6667C28.6193 26.6667 26.2623 26.6667 24.7978 28.1312C23.3333 29.5957 23.3333 31.9527 23.3333 36.6667" stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d="M27.5 3.33333V8.33333M12.5 3.33333V8.33333M20 3.33333V8.33333" stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                </svg>
                <h3 className="font-google-sans font-semibold text-[#121212] text-[1.25rem] leading-[1.3]"
                  style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                  Usage Limitations
                </h3>
              </div>
              <ul className="list-disc pl-5 font-lato text-[#686868] text-[clamp(0.9rem,1.1vw,1.1rem)] space-y-2 leading-[1.3]">
                <li>Mobile devices and tablets are not currently supported.</li>
                <li>Available only on desktop and laptop systems.</li>
                <li>Designed for widescreen layouts; touch devices may not provide full functionality.</li>
                <li>A broadband internet connection (10 Mbps or faster) is recommended for real-time parking updates.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Home page --------------------------------------
// --- Home page --------------------------------------

export default function HomePage() {
  useReveal();

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <ImageShowcaseSection />
        <SystemRequirementsSection />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}

